import * as React from "react";
import { useAuth } from "./hooks";
import { clearInviteToken, getInviteToken } from "./invite-pending";
import { getAuthCookie } from "./session-cookie";
import { capturePendingExternalRedirect } from "./external-redirect";
import { getCertificationAppOrigin } from "./app-origins";
import * as userApi from "../api/users";

/**
 * After email verification, accept a pending org invite (from invite link) so
 * the user is auto-joined and the organization step shows a member indicator.
 */
export function useClaimPendingOrganizationInvite(options?: {
  enabled?: boolean;
  onClaimed?: () => void;
}) {
  const { accessToken, user, refreshUser } = useAuth();
  const enabled = options?.enabled ?? true;
  const claimedRef = React.useRef(false);

  React.useEffect(() => {
    claimedRef.current = false;
  }, [user?.email, user?.emailVerified]);

  React.useEffect(() => {
    if (!enabled || !user?.emailVerified) return;

    const inviteToken = getInviteToken();
    const token = accessToken ?? getAuthCookie("access_token");
    if (!inviteToken || !token || claimedRef.current) return;

    claimedRef.current = true;
    void userApi
      .claimOrganizationInvite(token, inviteToken)
      .then(async (result) => {
        if (result.intent === "certification") {
          capturePendingExternalRedirect(getCertificationAppOrigin());
        }
        clearInviteToken();
        await refreshUser();
        options?.onClaimed?.();
      })
      .catch(() => {
        claimedRef.current = false;
      });
  }, [
    enabled,
    user?.emailVerified,
    user?.email,
    accessToken,
    refreshUser,
    options?.onClaimed,
  ]);
}
